import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Minus, Plus, ShoppingBag } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { imgUrl, fmtGram, lineHasParts } from "@/lib/format";
import { toast } from "sonner";
import SimilarProducts from "@/components/SimilarProducts";

function Spec({ label, value, accent }) {
  return (
    <div className="flex justify-between py-3 border-b border-border">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`text-sm font-medium ${accent ? "text-gold" : "text-foreground"}`}>{value}</span>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const { user } = useAuth();
  const hideMilyem = user?.hide_milyem;
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [sel, setSel] = useState(0);
  const [sizeQty, setSizeQty] = useState({});

  useEffect(() => {
    setSel(0);
    api.get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-gold" size={28} /></div>;
  if (!product) return <p className="text-center py-24 text-muted-foreground">Ürün bulunamadı.</p>;

  const outOfStock = product.stock_status === "Stokta Yok";
  const hasSizes = (product.sizes || []).length > 0;
  const imgs = product.images?.length ? product.images : (product.image_url ? [product.image_url] : []);
  const { pure, isc, total } = lineHasParts(product, 1);

  return (
    <div>
      <button onClick={() => navigate(-1)} data-testid="product-back" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-gold mb-4 transition-colors">
        <ArrowLeft size={16} /> Geri
      </button>

      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <div className="rounded-lg overflow-hidden border border-border bg-elevated aspect-square">
            {imgs.length ? (
              <img src={imgUrl(imgs[Math.min(sel, imgs.length - 1)])} alt={product.name} decoding="async" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">Görsel yok</div>
            )}
          </div>
          {imgs.length > 1 && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {imgs.map((u, i) => (
                <button key={i} onClick={() => setSel(i)} data-testid={`detail-thumb-${i}`} className={`w-16 h-16 rounded-md overflow-hidden border transition-colors ${sel === i ? "border-gold" : "border-border hover:border-gold/50"}`}>
                  <img src={imgUrl(u)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div data-testid="product-detail">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs bg-gold/15 text-gold border border-gold/30 px-2 py-1 rounded">{product.category}</span>
            <span className={`text-xs px-2 py-1 rounded border ${outOfStock ? "text-red-400 border-red-500/30 bg-red-500/10" : "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"}`}>
              {product.stock_status}
            </span>
          </div>
          <h1 className="font-display text-4xl text-foreground leading-tight">{product.name}</h1>
          <p className="font-mono text-sm text-muted-foreground mt-1">{product.code}</p>

          <div className="mt-6">
            <Spec label="Ayar" value={`${product.karat} Ayar`} />
            <Spec label="Tahmini Gram" value={product.gram_min && product.gram_max ? `~${fmtGram(product.gram_min)}–${fmtGram(product.gram_max)} gr` : `~${fmtGram(product.gram)} gr`} />
            <Spec label="Saf Has (ayar/24)" value={`${fmtGram(pure)} gr`} />
            {!hideMilyem && <Spec label="İşçilik (Milyem)" value={`${product.milyem || 0} milyem · ${fmtGram(isc)} gr`} />}
            <Spec label="Tahmini Toplam Has (adet)" value={`~${fmtGram(total)} gr`} accent />
            {product.subcategory && <Spec label="Alt Kategori" value={product.subcategory} />}
          </div>

          <div className="mt-3 flex items-start gap-2 text-[12px] text-amber-400/90 bg-amber-500/10 border border-amber-500/25 rounded-md px-3 py-2" data-testid="cold-sale-note">
            <span>Siparişe özel üretim: gramlar tahminidir, kesin has altın ürün üretilince belirlenir.</span>
          </div>

          {hasSizes && (
            <div className="mt-4 rounded-md border border-gold/25 bg-gold/5 p-3" data-testid="size-selector">
              <p className="text-sm text-gold font-semibold mb-2">Parmak Boyu · her boydan adet girin</p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <div key={s} className="flex items-center gap-1 border border-border rounded-md px-2 py-1 bg-obsidian">
                    <span className="text-sm text-muted-foreground w-8 text-center">{s}</span>
                    <button data-testid={`size-dec-${s}`} onClick={() => setSizeQty((q) => ({ ...q, [s]: Math.max(0, (q[s] || 0) - 1) }))} className="p-1 hover:text-gold"><Minus size={14} /></button>
                    <span data-testid={`size-qty-${s}`} className="w-6 text-center text-sm font-medium">{sizeQty[s] || 0}</span>
                    <button data-testid={`size-inc-${s}`} onClick={() => setSizeQty((q) => ({ ...q, [s]: (q[s] || 0) + 1 }))} className="p-1 hover:text-gold"><Plus size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {product.is_set && (product.set_parts || []).length > 0 && (
            <div className="mt-4 rounded-md border border-gold/25 bg-gold/5 p-3" data-testid="detail-set-parts">
              <p className="text-xs text-gold font-semibold mb-2">Set İçeriği</p>
              {product.set_parts.map((p, i) => (
                <div key={i} className="flex justify-between text-sm py-1 border-b border-border/40 last:border-0">
                  <span className="text-muted-foreground">{p.name || `Parça ${i + 1}`}</span>
                  <span className="text-foreground">{fmtGram(p.gram)} gr</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-4 mt-6">
            <div className={`flex items-center border border-border rounded-md ${hasSizes ? "hidden" : ""}`}>
              <button data-testid="qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:text-gold transition-colors"><Minus size={16} /></button>
              <span data-testid="qty-value" className="w-10 text-center font-medium">{qty}</span>
              <button data-testid="qty-increase" onClick={() => setQty(qty + 1)} className="p-3 hover:text-gold transition-colors"><Plus size={16} /></button>
            </div>
            <button
              data-testid="detail-add-to-cart"
              disabled={outOfStock}
              onClick={() => {
                if (hasSizes) {
                  const chosen = Object.entries(sizeQty).filter(([, q]) => q > 0);
                  if (chosen.length === 0) { toast.error("En az bir boy için adet girin"); return; }
                  chosen.forEach(([s, q]) => add(product, q, s));
                  toast.success("Sepete eklendi", { description: chosen.map(([s, q]) => `${s} boy × ${q}`).join(", ") });
                  setSizeQty({});
                } else {
                  add(product, qty); toast.success("Sepete eklendi", { description: `${qty} adet ${product.name}` });
                }
              }}
              className="flex-1 h-12 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ShoppingBag size={18} /> Sepete Ekle
            </button>
          </div>
        </div>
      </div>

      <SimilarProducts productId={product.id} />
    </div>
  );
}
